import Hls from 'hls.js';
import { pickVideoQuality } from './videoQuality';

export const isIOS = () =>
  typeof navigator !== 'undefined' &&
  (/iP(hone|od|ad)/.test(navigator.platform || '') ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document));

export const canPlayNativeHls = (video) =>
  !!video && !!video.canPlayType && video.canPlayType('application/vnd.apple.mpegurl') !== '';

let _h264;
// Can this browser decode H.264/AAC at all? (open-source Chromium builds and some kiosks can't)
export const canPlayH264 = () => {
  if (_h264 !== undefined) return _h264;
  try {
    const v = document.createElement('video');
    const tag = v.canPlayType('video/mp4; codecs="avc1.42E01E, mp4a.40.2"') !== '';
    const mse = !!window.MediaSource && MediaSource.isTypeSupported('video/mp4; codecs="avc1.64001f,mp4a.40.2"');
    _h264 = tag || mse || canPlayNativeHls(v);
  } catch (_) { _h264 = true; }
  return _h264;
};

// Ordered playback sources: HLS first, then progressive MP4s (network-aware order), then YouTube.
export const buildSources = (film) => {
  if (!film) return [];
  const out = [];
  const h264 = canPlayH264();
  if (film.hls_url && h264) out.push({ type: 'hls', url: film.hls_url, label: 'Adaptive' });
  if (h264) {
    const seen = new Set();
    const { url: preferred } = pickVideoQuality(film);
    [preferred, film.video_url, film.video_url_high, film.video_url_low].forEach((u) => {
      if (u && !seen.has(u)) { seen.add(u); out.push({ type: 'mp4', url: u, label: 'MP4' }); }
    });
  }
  if (film.youtube_id) out.push({ type: 'youtube', id: film.youtube_id, label: 'YouTube' });
  return out;
};

// Light-first: begin on a small rung so the first frame lands fast, then let ABR step up.
export const START_MAX_HEIGHT = 480;
export const pickStartLevel = (levels) => {
  if (!levels?.length) return -1;
  let best = 0;
  levels.forEach((l, i) => { if ((l.height || 0) <= START_MAX_HEIGHT && (l.height || 0) >= (levels[best].height || 0)) best = i; });
  return best;
};

export const hlsConfig = {
  enableWorker: true,
  lowLatencyMode: false,
  autoStartLoad: false,
  startLevel: 0,
  capLevelToPlayerSize: true,
  abrEwmaDefaultEstimate: 800_000,
  abrEwmaFastVoD: 2,
  abrEwmaSlowVoD: 8,
  abrBandWidthFactor: 0.85,
  abrBandWidthUpFactor: 0.65,
  maxBufferLength: 30,
  maxMaxBufferLength: 30,
  maxBufferSize: 30 * 1000 * 1000,
  backBufferLength: 30,
  maxBufferHole: 0.5,
  maxStarvationDelay: 2,
  maxLoadingDelay: 2,
  manifestLoadingMaxRetry: 3,
  levelLoadingMaxRetry: 4,
  fragLoadingMaxRetry: 5,
  fragLoadingRetryDelay: 600,
  startFragPrefetch: true,
  testBandwidth: false,
};

export const hlsSupported = () => Hls.isSupported();

// Attach an HLS source to a <video>. Returns a cleanup fn. onFatal(kind) called when hls.js gives up.
export const attachHls = (video, url, { onFatal, onReady, onLevel, onLevels }) => {
  if (canPlayNativeHls(video) && (isIOS() || !Hls.isSupported())) {
    video.src = url;
    const ready = () => onReady?.({ native: true });
    video.addEventListener('loadedmetadata', ready, { once: true });
    return () => { video.removeEventListener('loadedmetadata', ready); video.removeAttribute('src'); video.load(); };
  }
  if (!Hls.isSupported() || !(window.MediaSource && MediaSource.isTypeSupported('video/mp4; codecs="avc1.64001f,mp4a.40.2"'))) {
    onFatal?.('unsupported');
    return () => {};
  }

  const hls = new Hls(hlsConfig);
  let netRetries = 0;
  let mediaRetries = 0;
  hls.on(Hls.Events.MANIFEST_PARSED, () => {
    hls.startLevel = pickStartLevel(hls.levels);
    hls.startLoad(-1);
    onLevels?.(hls.levels.map((l, i) => ({ index: i, height: l.height, bitrate: l.bitrate })), hls);
    onReady?.({ native: false, levels: hls.levels.length });
  });
  hls.on(Hls.Events.LEVEL_SWITCHED, (_, d) => onLevel?.(hls.levels[d.level], d.level));
  hls.on(Hls.Events.ERROR, (_, data) => {
    if (!data.fatal) return;
    if (data.type === Hls.ErrorTypes.NETWORK_ERROR && netRetries < 2) {
      netRetries += 1;
      setTimeout(() => hls.startLoad(), 600 * netRetries);
      return;
    }
    if (data.type === Hls.ErrorTypes.MEDIA_ERROR && mediaRetries < 2) {
      mediaRetries += 1;
      if (mediaRetries === 2) hls.swapAudioCodec();
      hls.recoverMediaError();
      return;
    }
    onFatal?.(data.type);
  });
  hls.loadSource(url);
  hls.attachMedia(video);
  return () => { try { hls.destroy(); } catch (_) { /* noop */ } };
};

const POS_KEY = (id) => `cm_film_pos_${id}`;
export const savePosition = (id, t, d) => {
  if (!id || !d) return;
  try {
    if (t > 5 && t < d - 8) sessionStorage.setItem(POS_KEY(id), String(Math.floor(t)));
    else sessionStorage.removeItem(POS_KEY(id));
  } catch (_) { /* private mode */ }
};
export const readPosition = (id) => {
  try { return Number(sessionStorage.getItem(POS_KEY(id))) || 0; } catch (_) { return 0; }
};
export const clearPosition = (id) => { try { sessionStorage.removeItem(POS_KEY(id)); } catch (_) { /* noop */ } };

// ---- Early prefetch: master → light variant playlist → first N segments, all into the HTTP cache ----
const _prefetched = new Set();
const lowBandwidth = () => {
  const c = typeof navigator !== 'undefined' && (navigator.connection || navigator.mozConnection || navigator.webkitConnection);
  return !!(c && (c.saveData || /(^|-)2g/.test(c.effectiveType || '')));
};
const lowFetch = (url) => fetch(url, { priority: 'low', mode: 'cors', cache: 'force-cache', credentials: 'omit' });
const parseMaster = (txt, base) => {
  const lines = txt.split('\n').map((l) => l.trim());
  const out = [];
  for (let i = 0; i < lines.length; i += 1) {
    if (lines[i].startsWith('#EXT-X-STREAM-INF')) {
      const m = /RESOLUTION=(\d+)x(\d+)/.exec(lines[i]);
      const uri = lines.slice(i + 1).find((l) => l && !l.startsWith('#'));
      if (uri) out.push({ height: m ? Number(m[2]) : 0, url: new URL(uri, base).href });
    }
  }
  return out;
};

// Pull only the first `segments` chunks (≈4 s each) of the light rung for the film on screen. HLS only — never full MP4s.
export const prefetchFilm = async (film, { segments = 2, maxHeight = START_MAX_HEIGHT } = {}) => {
  if (!film?.hls_url || typeof window === 'undefined' || lowBandwidth() || !canPlayH264()) return;
  const key = `${film.id || film.hls_url}:${segments}`;
  if (_prefetched.has(key)) return;
  _prefetched.add(key);
  try {
    const master = await (await lowFetch(film.hls_url)).text();
    const variants = parseMaster(master, film.hls_url);
    if (!variants.length) return;
    const light = variants.filter((v) => v.height <= maxHeight).sort((a, b) => b.height - a.height)[0] || variants.sort((a, b) => a.height - b.height)[0];
    const playlist = await (await lowFetch(light.url)).text();
    const segs = playlist.split('\n').map((l) => l.trim()).filter((l) => l && !l.startsWith('#')).slice(0, segments);
    await Promise.all(segs.map((s) => lowFetch(new URL(s, light.url).href).catch(() => {})));
  } catch (_) { /* best effort */ }
};

// True while `el` is at least `threshold` on screen and the tab is visible.
export const observeOnScreen = (el, cb, threshold = 0.25) => {
  if (!el) return () => {};
  let inView = false;
  const emit = () => cb(inView && !document.hidden);
  const io = typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(([e]) => { inView = e.isIntersecting && e.intersectionRatio >= threshold; emit(); }, { threshold: [0, threshold, 1] })
    : null;
  if (io) io.observe(el); else { inView = true; emit(); }
  document.addEventListener('visibilitychange', emit);
  return () => { io?.disconnect(); document.removeEventListener('visibilitychange', emit); };
};
